import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { openModal, closeModal } from 'reducers/modals';
import { fetchCompaniesList } from 'reducers/staticData';
// components
import Link from 'react-router/lib/Link';
import { FormattedMessage } from 'react-intl';
import Button, { TransparentBtn } from 'components/Button';
import Panel from 'components/Panel';
import Preloader from 'components/Preloader';
import CustomGroupNameModal from '../components/CustomGroupNameModal';
import SingleDatePicker from 'components/DatePicker/SingleDatePicker/SingleDatePicker';
import DateRangePicker from 'components/DatePicker/DateRangePicker';
import AsyncSelectLoadMore from 'components/AsyncSelect/AsyncSelectLoadMore';
import ButtonRadioGroup from 'components/ButtonRadioGroup/ButtonRadioGroup';
import AttentionBox from 'components/AttentionBox/AttentionBox';
import Input from 'components/Input';
import SourcesSelector from '../components/SourcesSelector';
// utils
import { getInitials, formatFarmOptionLabel, showToastrMessage } from 'utils';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import isEmpty from 'lodash.isempty';
import moment from 'moment';
import classnames from 'classnames/bind';
import debounce from 'lodash.debounce';
import { formatPigGroupSources, getDefaultPigGroupSources, preparePigGroupSources } from 'utils/pigGroupsHelper';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { getInputPlaceholder, formatDateForUser } from 'utils/regionalDateHelper';
// api
import { searchCompanies } from 'endpoints/companies';
import { searchAdminFarms } from 'endpoints/admin/farms';
// groups api
import {
  createAdminPigGroup,
  deleteAdminPigGroup,
  getAdminPigGroup,
  publishAdminPigGroup,
  updateAdminPigGroup,
} from 'endpoints/admin/pigGroups';
// styles, assets
import styles from './GroupCreate.module.scss';
import groupInfoStyles from '../GroupProfile/GroupInfo.module.scss';

const cn = classnames.bind(styles);

const initialFields = {
  name: '',
  created_on: null,
  company: null,
  farm: null,
  delivery_start: null,
  delivery_end: null,
  source_entities: getDefaultPigGroupSources(),
  abf_tracking: null,
  initial_days_on_feed: '',
};

const abfOptions = [
  { label: <FormattedMessage id="general.button.no" />, value: false },
  { label: <FormattedMessage id="general.button.yes" />, value: true },
];

const isSourcesInvalid = (sources = []) => sources.some((source) => {
  return source.entity_type === 'internal' && source.farm?.id && !source.source_pig_group?.label;
});

class GroupCreate extends Component {

  constructor(props) {
    super(props);
    this.saveDraftDebounced = debounce(this.saveDraft, 600);
    this.isCancelRequested = false;
    this.state = {
      form: this.getInitialFormState(),
      groupIdPlaceholder: getInitials(props.user.current_company.name) + '-0000-0000',
      isLoading: false,
      isDraftSaving: false,
    };
  }

  componentWillUnmount() {
    this.saveDraftDebounced.cancel();
  }

  componentDidMount() {
    const { companies, fetchCompaniesList } = this.props;
    if (!companies.length) fetchCompaniesList();
    this.fetchPigGroupDraft();
  }

  componentDidUpdate(prevProps) {
    const { location: { query }, companies } = this.props;
    if (isEmpty(query) && query !== prevProps.location.query) {
      this.setState({
        form: this.getInitialFormState(),
      });
    }
    const companySelected = !!this.state.form.company?.value;
    const shouldAutoSelectCompany = !companySelected && companies.length === 1;
    const prevCompaniesLength = prevProps.companies.length;
    if (shouldAutoSelectCompany && companies.length !== prevCompaniesLength) {
      const [company] = companies;
      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          company: { value: company.id, label: company.name },
        },
      }));
    }
  }

  getInitialFormState = () => ({
    ...initialFields,
    created_on: moment().local(),
  });

  onCompanyChange = (company) => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        company: company || null,
      },
    }), this.saveDraftDebounced);
  };

  onFarmChange = (farm) => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        farm: farm || null,
      },
    }), this.saveDraftDebounced);
  };

  getInternalFarms = (params) => {
    const companyId = this.state.form.company?.value;
    return searchAdminFarms({
      ...params,
      only_internal: true,
      active: true,
      ...(companyId ? { manager_id: companyId } : {}),
    });
  };

  onDateChange = (created_on) => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        created_on,
      },
    }), this.saveDraftDebounced);
  };

  onDeliveryDateChange = ({ startDate, endDate }) => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        delivery_start: startDate,
        delivery_end: endDate,
      },
    }), this.saveDraftDebounced);
  };

  onClearDeliveryDateRange = () => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        delivery_start: null,
        delivery_end: null,
      },
    }), this.saveDraftDebounced);
  };

  onAbfChange = ({ target: { value } }) => {
    const parsedValue = (typeof value === 'string') ? JSON.parse(value) : value;
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        abf_tracking: parsedValue,
      },
    }), this.saveDraftDebounced);
  };

  onInitialDaysOnFeedChange = (e) => {
    e.persist();
    const reg = /^[0-9]{0,3}$/g;
    if (e.target.value === '' || reg.test(e.target.value)) {
      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          initial_days_on_feed: e.target.value,
        },
      }), this.saveDraftDebounced);
    }
  };

  onSourceChange = (source_entities) => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        source_entities: source_entities || getDefaultPigGroupSources(),
      },
    }));
  };

  onClearInitialDaysOnFeed = () => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        initial_days_on_feed: '',
      },
    }), this.saveDraftDebounced);
  };

  onCustomNameModalOpen = () => {
    const { closeModal, openModal } = this.props;
    const { form } = this.state;
    openModal(
      <CustomGroupNameModal
        initialName={form.name}
        onClose={closeModal}
        onSubmit={this.onNameChange}
      />
    );
  };

  onNameChange = (data) => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        name: data.name,
      },
    }), this.saveDraftDebounced);
  };

  formatFields = ({
    name,
    created_on,
    company,
    farm,
    delivery_start,
    delivery_end,
    source_entities,
    abf_tracking,
    initial_days_on_feed,
  }) => ({
    name: name || '',
    delivery_start: delivery_start ? moment(delivery_start) : null,
    delivery_end: delivery_end ? moment(delivery_end) : null,
    created_on: created_on ? moment(created_on) : moment().local(),
    company: company ? { value: company.id, label: company.name } : null,
    farm: farm ? { value: farm.id, label: formatFarmOptionLabel(farm), name: farm.name } : null,
    source_entities: source_entities?.length ? formatPigGroupSources(source_entities) : getDefaultPigGroupSources(),
    abf_tracking,
    initial_days_on_feed: initial_days_on_feed ? String(initial_days_on_feed) : '',
  });

  publishGroup = (draftId) => {
    return publishAdminPigGroup(draftId)
      .then(() => {
        showToastrMessage('component.toastr.group.wasCreated');
        this.props.router.push(`/groups/${draftId}/groupfeed`);
      });
  };

  fetchPigGroupDraft = () => {
    const { location: { query } } = this.props;
    if (!isEmpty(query)) {
      this.setState({ isLoading: true });
      getAdminPigGroup(query.draftId)
        .then((resource) => {
          this.setState({
            isLoading: false,
            form: this.formatFields(resource),
          });
        })
        .catch((response) => {
          this.setState({ isLoading: false });
          toastResponseErrors(response);
        });
    }
  };

  saveDraft = () => {
    const { location: { query, pathname } } = this.props;
    const payload = this.buildPayload();
    if (this.state.isDraftSaving) return;

    if (!query?.draftId) {
      this.setState({ isDraftSaving: true });
      createAdminPigGroup(payload)
        .then((resource) => {
          if (this.isCancelRequested) {
            return deleteAdminPigGroup(resource.id)
              .catch(() => null)
              .finally(() => {
                this.props.router.push('/groups');
              });
          }
          this.setState({
            form: this.formatFields(resource),
            isDraftSaving: false,
          });
          this.props.router.push({ pathname, query: { draftId: resource.id } });
          return null;
        })
        .catch((response) => {
          if (this.isCancelRequested) return;
          this.setState({ isDraftSaving: false });
          toastResponseErrors(response);
        });
      return;
    }

    this.setState({ isDraftSaving: true });
    updateAdminPigGroup(query.draftId, payload)
      .then((resource) => {
        this.setState({
          form: this.formatFields(resource),
          isDraftSaving: false,
        });
      })
      .catch((response) => {
        if (this.isCancelRequested) return;
        this.setState({ isDraftSaving: false });
        toastResponseErrors(response);
      });
  };

  onCancel = (e) => {
    e.preventDefault();
    const { location: { query }, router } = this.props;
    this.isCancelRequested = true;
    this.saveDraftDebounced.cancel();

    if (query?.draftId) {
      deleteAdminPigGroup(query.draftId)
        .catch(() => null)
        .finally(() => {
          router.push('/groups');
        });
      return;
    }

    router.push('/groups');
  };

  onFinishLater = (e) => {
    e.preventDefault();
    const { location: { query }, router } = this.props;
    if (!query?.draftId) {
      router.push('/groups');
      return;
    }

    this.saveDraftDebounced.cancel();
    this.setState({ isDraftSaving: true });
    updateAdminPigGroup(query.draftId, this.buildPayload())
      .then(() => {
        router.push('/groups');
      })
      .catch(toastResponseErrors)
      .finally(() => {
        this.setState({ isDraftSaving: false });
      });
  };

  buildPayload = () => {
    const isDraftUpdate = Boolean(this.props.location?.query?.draftId);
    const {
      name,
      created_on,
      company,
      farm,
      delivery_start,
      delivery_end,
      source_entities,
      abf_tracking,
      initial_days_on_feed,
    } = this.state.form;
    const companyPayload = {};
    if (isDraftUpdate) {
      companyPayload.company_id = company?.value || null;
    } else if (company?.value) {
      companyPayload.company_id = company.value;
    }

    const farmPayload = {};
    if (isDraftUpdate) {
      farmPayload.farm_id = farm?.value || null;
    } else if (farm?.value) {
      farmPayload.farm_id = farm.value;
    }

    const deliveryStartPayload = {};
    if (isDraftUpdate) {
      deliveryStartPayload.delivery_start = delivery_start || null;
    } else if (delivery_start) {
      deliveryStartPayload.delivery_start = delivery_start;
    }

    const deliveryEndPayload = {};
    if (isDraftUpdate) {
      deliveryEndPayload.delivery_end = delivery_end || null;
    } else if (delivery_end) {
      deliveryEndPayload.delivery_end = delivery_end;
    }

    return {
      ...(name ? { name } : {}),
      ...(created_on ? { created_on: created_on.format('YYYY-MM-DD') } : {}),
      ...companyPayload,
      ...farmPayload,
      ...deliveryStartPayload,
      ...deliveryEndPayload,
      ...(!this.props.isLogisticsEnabled ? {
        source_entities_attributes: preparePigGroupSources(source_entities),
      } : {}),
      ...(abf_tracking !== null ? { abf_tracking } : {}),
      initial_days_on_feed: Number(initial_days_on_feed || 0),
    };
  };

  handleSubmit = () => {
    const { location: { query } } = this.props;
    const payload = this.buildPayload();
    if (!payload.company_id || !payload.farm_id) return;

    this.setState({ isLoading: true });
    const request = query.draftId
      ? updateAdminPigGroup(query.draftId, payload)
      : createAdminPigGroup(payload);

    request
      .then((resource) => this.publishGroup(resource.id))
      .catch((response) => {
        this.setState({ isLoading: false });
        toastResponseErrors(response);
      });
  };

  render() {
    const { isLoading, form, groupIdPlaceholder, isDraftSaving } = this.state;
    const { isLogisticsEnabled, isABFEnabled, countryCode, location: { query } } = this.props;
    const {
      created_on,
      company,
      farm,
      delivery_start,
      delivery_end,
      abf_tracking,
      name,
      initial_days_on_feed,
    } = form;
    const canSubmit = !isEmpty(company)
      && !isEmpty(farm)
      && (isLogisticsEnabled || !isSourcesInvalid(form.source_entities));
    const regionalPlaceholder = getInputPlaceholder(countryCode);
    const showDeliveryInfo = delivery_start && delivery_end;
    const showDeliveryClear = delivery_start || delivery_end;
    const showInitialDaysOnFeedClear = Boolean(String(initial_days_on_feed).length);
    const isDraft = Boolean(query?.draftId);
    const farmSelectKey = company?.value ? `farm-select-${company.value}` : 'farm-select-all';
    const showAbfSection = isABFEnabled;
    let abfLabel = <FormattedMessage id="general.isAnAbfGroup" />;
    if (abf_tracking === true) {
      abfLabel = <FormattedMessage id="general.activeAbfGroup" />;
    }
    if (abf_tracking === false) {
      abfLabel = <FormattedMessage id="general.groupIsNotABF" />;
    }

    return (
      <div>
        <section className="small-12 column">
          <div className={cn('group-header')}>
            <div className={cn('name-block')}>
              <span className={cn('name')}>
                {name || groupIdPlaceholder}
                {isDraft && (
                  <span className={cn('badge')}>
                    <FormattedMessage id="general.status.draft" />
                  </span>
                )}
              </span>
              <span className={cn('add-custom-group-btn')} onClick={this.onCustomNameModalOpen}>
                <FormattedMessage id="component.groupCreate.useCustomGroupId" />
              </span>
            </div>
            {isDraft && (
              <div className={cn('actions-block')}>
                <Link className={cn('finish-later-btn')} to="/groups" onClick={this.onFinishLater}>
                  <FormattedMessage id="general.button.finishLater" />
                </Link>
              </div>
            )}
          </div>
        </section>
        <section className="small-12 column">
          <Panel>
            <Panel.Body noPadding>
              <Preloader isActive={isLoading} />
              <div className={groupInfoStyles['group-field']}>
                <div className={groupInfoStyles.title}>
                  <FormattedMessage id="component.groupCreate.groupCreationDate" />
                </div>
                <SingleDatePicker
                  date={created_on}
                  onDateChange={this.onDateChange}
                  inputLabel={regionalPlaceholder}
                  isOutsideRange={(day) => (day.local().isAfter(moment().local(), 'day'))}
                  enableOutsideDays
                  placeholder={regionalPlaceholder}
                />
              </div>

              <div className={groupInfoStyles['group-field']}>
                <div className={groupInfoStyles.title}>
                  <FormattedMessage id="component.groupCreate.groupOwner" />
                </div>
                <AsyncSelectLoadMore
                  className="small-12 medium-6"
                  selected={company?.value ? company : null}
                  isClearable
                  optionsGetter={searchCompanies}
                  placeholder={<FormattedMessage id="general.searchBy.companyName" />}
                  onChange={this.onCompanyChange}
                />
              </div>

              <div className={groupInfoStyles['group-field']}>
                <div className={groupInfoStyles.title}>
                  <FormattedMessage id="general.farm" />
                </div>
                <AsyncSelectLoadMore
                  className="small-12 medium-6"
                  key={farmSelectKey}
                  selected={farm?.value ? farm : null}
                  isClearable
                  labelRenderer={formatFarmOptionLabel}
                  onChange={this.onFarmChange}
                  optionsGetter={this.getInternalFarms}
                  placeholder={<FormattedMessage id="general.searchBy.farmNameOrId" />}
                />
              </div>

              {!isLogisticsEnabled && (
                <>
                  <div className={groupInfoStyles['group-field']}>
                    <div className={groupInfoStyles.title}>
                      <FormattedMessage id="component.groupCreate.estimatedDateRange" />
                    </div>
                    <div className={cn('small-12 medium-6', 'delivery-date-range-row')}>
                      <DateRangePicker
                        className={cn('delivery-date-range-picker')}
                        startDate={delivery_start}
                        endDate={delivery_end}
                        isOutsideRange={(day) => (day.isBefore(moment(), 'day'))}
                        onDatesChange={this.onDeliveryDateChange}
                      />
                      {showDeliveryClear && (
                        <TransparentBtn className={cn('clear-action-btn')} onClick={this.onClearDeliveryDateRange}>
                          <FormattedMessage id="general.button.clear" />
                        </TransparentBtn>
                      )}
                    </div>
                    {showDeliveryInfo && (
                      <div className="pt-10">
                        <AttentionBox>
                          <FormattedMessage
                            id="component.groupCreate.dateRangeInfo"
                            values={{ date: formatDateForUser(delivery_start, countryCode) }}
                          />
                        </AttentionBox>
                      </div>
                    )}
                  </div>
                </>
              )}

              {showAbfSection && (
                <div className={groupInfoStyles['group-field']}>
                  <span className={groupInfoStyles.title}>
                    <FormattedMessage id="general.abfGroup" />
                  </span>
                  <div className={cn('small-12 medium-6', groupInfoStyles['abf-field'])}>
                    {abfLabel}
                    <ButtonRadioGroup
                      name="abf_tracking"
                      className={groupInfoStyles['radio-button-group']}
                      options={abfOptions}
                      value={abf_tracking}
                      onChange={this.onAbfChange}
                    />
                  </div>
                </div>
              )}
              {!isLogisticsEnabled && (
                <div className={groupInfoStyles['group-field']}>
                  <div className={groupInfoStyles.title}>
                    <FormattedMessage id="general.source" />
                  </div>
                  <SourcesSelector
                    className="small-12 medium-6"
                    onChange={this.onSourceChange}
                    sources={form.source_entities}
                  />
                </div>
              )}
              <div className={groupInfoStyles['group-field']}>
                <div className={groupInfoStyles.title}>
                  <FormattedMessage id="general.daysOnFeedCorrection" />
                  <p className={groupInfoStyles['dof-info']}>
                    <FormattedMessage id="general.daysOnFeedCorrectionInfo" />
                  </p>
                </div>
                <div className={cn('small-12 medium-6', 'days-on-feed-row')}>
                  <Input
                    type="text"
                    value={initial_days_on_feed}
                    onChange={this.onInitialDaysOnFeedChange}
                    placeholder="0"
                    hasNoMargin
                    className={cn('days-on-feed-input')}
                  />
                  {showInitialDaysOnFeedClear && (
                    <TransparentBtn className={cn('clear-action-btn')} onClick={this.onClearInitialDaysOnFeed}>
                      <FormattedMessage id="general.button.clear" />
                    </TransparentBtn>
                  )}
                </div>
              </div>
              <div className={groupInfoStyles['group-field']}>
                <Button
                  primary
                  onClick={this.handleSubmit}
                  disabled={!canSubmit || isLoading || isDraftSaving}
                >
                  <FormattedMessage id="general.button.createGroup" />
                </Button>
                <Link to="/groups" className="button btn-link margin-left-1" onClick={this.onCancel}>
                  <FormattedMessage id="general.button.cancel" />
                </Link>
              </div>
            </Panel.Body>
          </Panel>
        </section>
      </div>
    );
  }
}

GroupCreate.propTypes = {
  user: T.object,
  location: T.object.isRequired,
  router: T.object.isRequired,
  openModal: T.func.isRequired,
  closeModal: T.func.isRequired,
  isLogisticsEnabled: T.bool.isRequired,
  isABFEnabled: T.bool.isRequired,
  countryCode: T.string,
  companies: T.array.isRequired,
  fetchCompaniesList: T.func.isRequired,
};

export default connect(
  (state) => ({
    isLogisticsEnabled: state.auth.user.current_company.logistics,
    isABFEnabled: state.auth.user.current_company.abf_tracking,
    user: state.auth.user,
    countryCode: getCurrentCountryCode(state),
    companies: state.staticData.companiesList.resources,
  }), { openModal, closeModal, fetchCompaniesList }
)(GroupCreate);
